<template>
  <div class="v-collapse-item" :class="{ 'is-active': isActive, 'is-disabled': disabled }">
    <div class="v-collapse-item__header" role="tab" :style="{ height: px(collapse.props.headerHeight) }" @click="toggle" @keyup.space.enter.stop="toggle">
      <VIcon v-if="!collapse.props.arrowRight" class="v-collapse-item__arrow" icon="ChevronRight" :size="12" style="margin-right: 10px" type="info" />

      <div class="v-collapse-item__title">
        <slot name="title">{{ title }}</slot>
      </div>

      <div v-if="$slots['header-right']" class="v-collapse-item__header-right">
        <slot name="header-right" />
      </div>

      <VIcon v-if="collapse.props.arrowRight" class="v-collapse-item__arrow" icon="ChevronRight" :size="12" style="margin-left: 20px" type="info" />
    </div>

    <VTransitionCollapse :lazy="lazy !== undefined ? lazy : collapse.props.lazy !== undefined ? collapse.props.lazy : true" :visible="isActive">
      <div class="v-collapse-item__body" role="tabpanel">
        <slot />
      </div>
    </VTransitionCollapse>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { px } from '../../utils/helpers.ts'
import { $collapseKey, injectRequired } from '../../utils/injections.ts'
import VIcon from '../Icon/VIcon.vue'
import VTransitionCollapse from '../VTransitionCollapse.vue'

interface Props {
  title?: string
  name?: string | number
  disabled?: boolean
  lazy?: boolean
}

const { title = '', name = id++, disabled = false } = defineProps<Props>()

defineSlots<{
  default?(): any
  title?(): any
  'header-right'?(): any
}>()

const collapse = injectRequired($collapseKey)

const isActive = computed(() => {
  if (Array.isArray(collapse.model.value)) {
    return collapse.model.value.includes(name)
  }

  return collapse.model.value === name
})

function toggle() {
  if (disabled || !collapse) return

  const value = collapse.model.value

  if (Array.isArray(value)) {
    const newValue = [...value]
    const index = value.indexOf(name)
    if (index >= 0) {
      newValue.splice(index, 1)
    } else {
      newValue.push(name)
    }
    collapse.model.value = newValue
  } else if (value === name) {
    collapse.model.value = ''
  } else {
    collapse.model.value = name
  }
}

defineExpose({
  toggle,
})
</script>

<script lang="ts">
let id = 0
</script>

<style>
.v-collapse-item__header {
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-top: -1px;
  border-top: var(--border);
}

.v-collapse-item__title {
  flex-grow: 1;
}

.v-collapse-item__body {
  margin-bottom: 20px;
}

.v-collapse-item.is-active .v-collapse-item__arrow {
  transform: rotate(90deg);
}

.v-collapse-item.is-disabled {
  pointer-events: none;

  .v-collapse-item__header {
    cursor: default;
  }

  .v-collapse-item__arrow {
    visibility: hidden;
  }
}
</style>
